import { Component, computed, forwardRef, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

import { TrackingStoreService } from './services/tracking-store.service';
import { TrackingSocketService } from './services/tracking-socket.service';
import { TrackingApiService } from './services/tracking-api.service';
import {
  ProductConfigService, type TrackingDemoProductConfig,
} from '../../../core/services/product-config.service';
import { ToasterService } from '../../../core/services/toaster.service';
import { AuthService } from '../../../core/services/auth.service';
import { ConfirmationModalComponent } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

import { SessionMetricsComponent } from './components/session-metrics.component';
import { LiveSessionTableComponent, SessionAction } from './components/live-session-table.component';
import { SessionDetailsPanelComponent } from './components/session-details-panel.component';
import { SessionHistoryComponent } from './components/session-history.component';
import { SessionReportComponent } from './components/session-report.component';
import { BlacklistManagerComponent } from './components/blacklist-manager.component';
import { AuditLogsComponent } from './components/audit-logs.component';
import { PresenceToggleComponent } from './components/presence-toggle.component';
import { FailedQueriesComponent } from './components/failed-queries.component';
import { InsightsDashboardComponent } from './components/insights-dashboard.component';
import { PrejoinDumpsComponent } from './components/prejoin-dumps.component';
import { PrejoinLiveComponent } from './components/prejoin-live.component';
import { LiveScreenViewerComponent } from './components/live-screen-viewer.component';
import { parseApiMs } from './tracking-format.util';
import {
  ALL_PRODUCTS_CONFIG, ALL_PRODUCTS_KEY, isAllProductsPanel, trackingProductTabs,
} from './tracking-product-tabs';
import type { DisconnectReason, TrackingSession, TrackingSessionStatus } from './tracking-demo.models';

type Tab = 'live' | 'history' | 'failed' | 'blacklist' | 'audit' | 'insights' | 'dumps';
type PanelTab = 'details' | 'participants';

/**
 * Prism "Tracking Demo" admin dashboard — monitors every live AI demo in real time,
 * lets an admin JOIN a live demo as a hidden observer, force-disconnects, and manages
 * the IP blacklist.
 *
 * Registered in COMPONENT_REGISTRY and exposed via Menu & Roles for admin roles.
 * Store + socket + api are provided at the component so they die on route leave.
 *
 * MULTI-PRODUCT: the same component serves every product panel (Credee, Practina, …).
 * The dynamic route builder stamps `productName` into route data; it resolves to a
 * TrackingDemoProductConfig whose productKey scopes every API call + the WS reducer,
 * and whose observer fields drive the per-product covert-join flow.
 */
@Component({
  selector: 'app-tracking-demo',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    SessionMetricsComponent, LiveSessionTableComponent, SessionDetailsPanelComponent,
    SessionHistoryComponent, SessionReportComponent, BlacklistManagerComponent, AuditLogsComponent,
    PresenceToggleComponent, InsightsDashboardComponent, PrejoinDumpsComponent,
    LiveScreenViewerComponent, PrejoinLiveComponent,
    // forwardRef: FailedQueriesComponent injects TrackingApiService, which closes the
    // same circular-import loop as the providers entry below (see note there) — a
    // plain reference here trips the same TDZ in an isolated module graph.
    forwardRef(() => FailedQueriesComponent),
  ],
  // TrackingApiService is forwardRef-wrapped: this component is itself listed in
  // COMPONENT_REGISTRY (core/registry/component-registry.ts, read by AuthService for
  // dynamic routing), and TrackingApiService imports AuthService — a real circular
  // import (tracking-api.service -> auth.service -> component-registry ->
  // tracking-demo.component -> tracking-api.service). Reading the plain class
  // reference here at decorator-eval time trips that cycle's TDZ in an isolated
  // module graph (e.g. a component spec that imports TrackingApiService alone,
  // without the rest of the app's bundle order masking it). forwardRef defers the
  // read to actual DI resolution time, by which point the cycle has unwound.
  providers: [
    TrackingStoreService, TrackingSocketService,
    { provide: forwardRef(() => TrackingApiService), useClass: forwardRef(() => TrackingApiService) },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './tracking-demo.component.html',
})
export class TrackingDemoComponent {
  readonly store = inject(TrackingStoreService);
  private readonly api = inject(TrackingApiService);
  private readonly dialog = inject(MatDialog);
  private readonly toaster = inject(ToasterService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  // QA_AUDITORS is report-only: no Live tab (and with it no observer join /
  // disconnect / blacklist-from-row actions). History / Blacklist / Audit stay.
  readonly tabs: Tab[] = this.auth.hasRole('QA_AUDITORS')
    ? ['history', 'failed', 'insights', 'dumps']
    : ['live', 'history', 'failed', 'blacklist', 'audit', 'insights', 'dumps'];
  readonly tab = signal<Tab>(this.tabs[0]);
  /**
   * Per-product dashboard config, resolved from the route data the dynamic route builder
   * stamps (`productName`). Snapshot is safe: each product panel is its own route subtree,
   * so switching products recreates the component. Absent/unknown → Credee (legacy).
   */
  private readonly productConfigs = inject(ProductConfigService);
  private readonly routeProduct: string | undefined = this.route.snapshot.data['productName'];
  private readonly panelCfg = this.productConfigs.getTrackingDemoConfig(this.routeProduct);
  /** Mounted under the "Prism" group: the pre-join live board only, for every
   *  product at once with a tab per product (no metrics / sessions / other tabs). */
  readonly allProductsPanel = isAllProductsPanel(this.routeProduct);
  readonly productTabs = this.allProductsPanel
    ? trackingProductTabs(this.productConfigs.getProductConfigs()) : [];
  readonly activeProduct = signal(this.allProductsPanel ? ALL_PRODUCTS_KEY : this.panelCfg.productKey);

  /** The config of the product on show (the selected tab on the all-products panel). */
  get trackingCfg(): TrackingDemoProductConfig {
    if (!this.allProductsPanel) return this.panelCfg;
    return this.productTabs.find((t) => t.key === this.activeProduct())?.config ?? ALL_PRODUCTS_CONFIG;
  }

  /** Covert observer JOIN needs a per-product call page + handoff key; hide it otherwise. */
  get joinEnabled(): boolean {
    return !!(this.trackingCfg.observerCallPath && this.trackingCfg.observerHandoffKeyPrefix);
  }

  /** All-products panel: show one product (or all again). The API calls follow
   *  productKey; the pre-join list re-creates itself (see the template). */
  selectProduct(key: string): void {
    if (key === this.activeProduct()) return;
    this.activeProduct.set(key);
    this.api.productKey.set(key);
  }


  readonly panel = signal<{ sessionId: string; tab: PanelTab } | null>(null);
  /** Session whose host screen is open in the "Watch live screen" modal. */
  readonly screenSession = signal<TrackingSession | null>(null);
  /** Session whose full report is open (full-screen overlay over the dashboard);
   *  mirrored in the URL as `?sessionId=` (null clears the param). */
  readonly reportSid = signal<string | null>(null);

  openReport(s: TrackingSession): void { this.reportSid.set(s.sessionId); this.syncReportParam(); }
  closeReport(): void { this.reportSid.set(null); this.syncReportParam(); }

  private syncReportParam(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sessionId: this.reportSid() },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  readonly statusFilterOptions: Array<{ label: string; value: 'ALL' | TrackingSessionStatus }> = [
    { label: 'All', value: 'ALL' },
    { label: 'Live', value: 'live' },
    { label: 'Waiting', value: 'waiting' },
    { label: 'Connecting', value: 'connecting' },
    { label: 'On Hold', value: 'on_hold' },
  ];

  readonly connectionLabel = computed(() => {
    switch (this.store.connectionState()) {
      case 'live': return { text: 'Live', cls: 'text-emerald-600', dot: 'bg-emerald-500 animate-pulse' };
      case 'connecting': return { text: 'Connecting', cls: 'text-sky-600', dot: 'bg-sky-500 animate-pulse' };
      case 'reconnecting': return { text: 'Reconnecting', cls: 'text-amber-600', dot: 'bg-amber-500 animate-pulse' };
      case 'unauthorized': return { text: 'Unauthorized', cls: 'text-red-600', dot: 'bg-red-500' };
      default: return { text: 'Offline', cls: 'text-gray-500', dot: 'bg-gray-400' };
    }
  });

  constructor() {
    // Reload / shared link with ?sessionId= → reopen that report (it lives on History).
    const sid = this.route.snapshot.queryParamMap.get('sessionId');
    if (sid) { this.reportSid.set(sid); this.tab.set('history'); }

    // Scope BEFORE start(): the first resync + every WS-driven call must already carry
    // this panel's product.
    this.api.productKey.set(this.trackingCfg.productKey);
    // OPEN access: the Prism CRM already authorizes the user, so start the live store
    // immediately. (If a backend admin token is ever configured, the connection chip
    // shows Unauthorized — TrackingAdminTokenService is where a token gate would plug in.)
    // The Prism panel is only the pre-join board, which polls on its own.
    if (!this.allProductsPanel) this.store.start();
  }

  // ── live table actions ──
  onRowClick(s: TrackingSession): void { this.openPanel(s.sessionId, 'details'); }

  onAction(evt: { action: SessionAction; session: TrackingSession }): void {
    const s = evt.session;
    switch (evt.action) {
      case 'view': this.openPanel(s.sessionId, 'details'); break;
      case 'screen': this.screenSession.set(s); break;
      case 'join': void this.openObserver(s); break;
      case 'participants': this.openPanel(s.sessionId, 'participants'); break;
      case 'disconnect': this.confirmDisconnect(s); break;
      case 'blacklist': this.confirmBlacklist(s); break;
    }
  }

  openPanel(sessionId: string, tab: PanelTab): void { this.panel.set({ sessionId, tab }); }
  closePanel(): void { this.panel.set(null); }

  /**
   * Join a live demo as a covert observer. Mints the sid-scoped observer token, stashes it
   * (with the admin token + resolved ROOM sid) in a one-shot, nonce-keyed localStorage entry so
   * nothing sensitive rides the URL, then opens the product's real demo call page in observer
   * mode (page + handoff key come from the per-product trackingDemo config).
   */
  async openObserver(s: TrackingSession): Promise<void> {
    if (!this.joinEnabled) {
      this.toaster.error('Observer join is not available for this product yet.', 'Observer');
      return;
    }
    try {
      const res = await this.api.mintObserverToken(s.sessionId);
      if (!res?.observerToken) throw new Error('no token');
      const nonce = crypto.randomUUID();
      const handoff = {
        observerToken: res.observerToken,
        roomSessionId: res.roomSessionId || s.sessionId,
        adminToken: this.api.adminToken(),
        // Attribution for the observer tab's control REST — its audit rows should
        // name this admin, not the generic "Prism Admin".
        adminName: this.api.adminName(),
        adminId: this.api.adminId(),
        // The meeting's real start (epoch ms) — the observer tab seeds its
        // "Live · m:ss" ticker from this so it matches the dashboard's duration
        // column instead of restarting at 0:00 when the admin joins.
        startedAtMs: parseApiMs(s.startedAt),
      };
      try {
        localStorage.setItem(
          `${this.trackingCfg.observerHandoffKeyPrefix}:${nonce}`, JSON.stringify(handoff));
      } catch {
        this.toaster.error('Could not open the observer view (storage blocked).', 'Observer');
        return;
      }
      const url = new URL(this.trackingCfg.observerCallPath!, location.origin);
      // The SESSION's product wins over the panel's config token: the observer tab must
      // open under the product this demo actually runs as, not the dashboard it's listed on.
      if (s.productKeyname) url.searchParams.set('token', s.productKeyname);
      url.searchParams.set('role', 'observer');
      url.searchParams.set('sid', handoff.roomSessionId);
      url.searchParams.set('handoff', nonce);
      window.open(url.toString(), '_blank', 'noopener');
    } catch (e) {
      // Surface the backend's actual refusal (e.g. UNREACHABLE "Meeting has ended" vs
      // "No active room for this session") instead of one catch-all guess — the
      // distinction is what tells apart an ended demo from a room/link problem.
      const body = (e as { error?: { code?: string; error?: string } } | null)?.error;
      const detail = typeof body?.error === 'string' && body.error ? body.error : '';
      this.toaster.error(
        detail
          ? `Could not join this demo — ${detail}${body?.code ? ` (${body.code})` : ''}.`
          : 'Could not join this demo — it may have just ended.',
        'Observer');
    }
  }

  // ── disconnect ──
  async confirmDisconnect(s: TrackingSession): Promise<void> {
    const result: any = await firstValueFrom(this.dialog.open(ConfirmationModalComponent, {
      width: '460px',
      data: {
        title: 'Disconnect this live session?',
        description: `${s.companyName || s.hostName || 'Session'} — ${s.sessionId}`,
        variant: 'danger',
        confirmLabel: 'Disconnect',
        showDropdown: true,
        dropdownLabel: 'Reason',
        dropdownOptions: [
          { label: 'Suspicious Activity', value: 'suspicious_activity' },
          { label: 'Manual Disconnect', value: 'manual' },
          { label: 'Security Issue', value: 'security_issue' },
          { label: 'Other', value: 'other' },
        ],
        showInput: true,
        inputPlaceholder: 'Optional note…',
      },
    }).afterClosed());
    if (!result?.confirmed) return;
    try {
      await this.api.disconnect(s.sessionId, (result.dropdownValue || 'manual') as DisconnectReason, result.note);
      this.toaster.success('Session disconnected');
      // Row convergence arrives via the admin_disconnected/session_ended WS event.
    } catch {
      this.toaster.error('Failed to disconnect session');
    }
  }

  // ── blacklist ──
  async confirmBlacklist(s: TrackingSession): Promise<void> {
    const ip = s.security?.ip;
    if (!ip) { this.toaster.warning('No IP captured for this session'); return; }
    const result: any = await firstValueFrom(this.dialog.open(ConfirmationModalComponent, {
      width: '460px',
      data: {
        title: 'Blacklist this IP?',
        description: `${ip} will be blocked from starting or joining demos.`,
        variant: 'danger',
        confirmLabel: 'Blacklist',
        showInput: true,
        inputRequired: true,
        inputPlaceholder: 'Reason (required)',
      },
    }).afterClosed());
    if (!result?.confirmed) return;
    try {
      await this.api.blacklistIp(ip, result.note || 'blacklisted from dashboard', s.sessionId);
      this.toaster.success(`${ip} blacklisted`);
    } catch {
      this.toaster.error('Failed to blacklist IP');
    }
  }

  panelSession(): TrackingSession | null {
    const p = this.panel();
    if (!p) return null;
    return this.store.sessions().find((s) => s.sessionId === p.sessionId) || null;
  }
}
